import { type AgreeToTermsPolicy } from '@logto/schemas';
import classNames from 'classnames';
import { type TFuncKey } from 'i18next';
import { useContext, useMemo, type ReactNode } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import BrandingHeader from '@/components/BrandingHeader';
import type { Props as TextLinkProps } from '@/components/TextLink';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useTerms from '@/hooks/use-terms';
import DynamicT from '@/shared/components/DynamicT';
import type { Props as PageMetaProps } from '@/shared/components/PageMeta';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import FirstScreenLayout from '../FirstScreenLayout';

import styles from './index.module.scss';

type Props = {
  readonly children: ReactNode;
  readonly pageMeta: PageMetaProps;
  readonly title: TFuncKey;
  readonly description: string;
  readonly footerTermsDisplayPolicies?: AgreeToTermsPolicy[];
  // eslint-disable-next-line react/no-unused-prop-types -- kept for upstream compatibility; the link is hidden below
  readonly authOptionsLink: TextLinkProps;
};

/**
 * FocusedAuthPageLayout Component
 *
 * This layout component is designed for focused authentication pages that serve as the first screen
 * for specific auth methods, such as identifier sign-in, identifier-register, and single sign-on landing pages.
 */
const FocusedAuthPageLayout = ({
  children,
  pageMeta,
  title,
  description,
  footerTermsDisplayPolicies = [],
}: Props) => {
  const { agreeToTermsPolicy } = useTerms();
  const { experienceSettings, theme } = useContext(PageContext);

  const shouldDisplayFooterTerms = useMemo(
    () => agreeToTermsPolicy && footerTermsDisplayPolicies.includes(agreeToTermsPolicy),
    [agreeToTermsPolicy, footerTermsDisplayPolicies]
  );

  return (
    <FirstScreenLayout pageMeta={pageMeta}>
      {experienceSettings && (
        <BrandingHeader
          className={classNames(styles.logo, layoutClassNames.brandingHeader)}
          logo={getBrandingLogoUrl({
            theme,
            branding: experienceSettings.branding,
            isDarkModeEnabled: experienceSettings.color.isDarkModeEnabled,
          })}
        />
      )}
      <div className={styles.header}>
        <div className={styles.title}>
          <DynamicT forKey={title} />
        </div>
        <div className={styles.description}>{description}</div>
      </div>
      {children}
      {shouldDisplayFooterTerms && <TermsAndPrivacyLinks className={styles.terms} />}
      {/* RouteCTRL: the all-sign-in-options link is hidden on focused pages */}
      {/* <TextLink {...authOptionsLink} className={styles.link} /> */}
    </FirstScreenLayout>
  );
};

export default FocusedAuthPageLayout;
